import {
  ErrorCode,
  Purchases,
  PurchasesError,
  ReservedCustomerAttribute,
  type CustomerInfo,
  type Package,
} from '@revenuecat/purchases-js';
import { ApiError, createCheckout, reactivateBilling } from './api.js';

/**
 * Web purchases, from the browser, through RevenueCat's Stripe Billing integration.
 *
 * **Stripe is the merchant of record** through Managed Payments: it appears on the
 * customer's statement and calculates, collects and remits sales tax and VAT. RevenueCat
 * still tracks the subscription, and the same entitlement identifier backs the App Store
 * and Play Store products, so `getCustomerInfo()` looks identical regardless of where the
 * household bought.
 *
 * Mirrors `apps/mobile/src/lib/billing.ts` deliberately: same init/logIn/purchase/restore
 * shape, different SDK underneath.
 */

/** The entitlement identifier configured in the RevenueCat dashboard. */
export const PRO_ENTITLEMENT_ID = 'platewell_pro';

/** The offering identifier configured in the RevenueCat dashboard. */
export const PRO_OFFERING_ID = 'pro';

const API_KEY: string | undefined = import.meta.env.VITE_REVENUECAT_WEB_BILLING_KEY;

export function isBillingConfigured(): boolean {
  return typeof API_KEY === 'string' && API_KEY !== '';
}

export interface BuyerProfile {
  id: string;
  name?: string | null;
  email?: string | null;
}

/**
 * Who is signed in, remembered for the moment a purchase is made.
 *
 * Held here rather than sent at sign-in because RevenueCat refuses attributes for a
 * customer it has not created yet, and most people who open the app never buy anything.
 * `purchaseWithIntent` sends the lot in one awaited call, where a failure is already
 * handled.
 */
let buyer: BuyerProfile | null = null;

/**
 * Identifies the SDK as this Platewell user.
 *
 * The App User ID is the Platewell user id, never an anonymous one: an anonymous
 * subscriber cannot be recovered after a browser change or on a second device, and the
 * webhook would have nothing to bind the purchase to.
 */
export function signIn(user: BuyerProfile): Purchases | null {
  buyer = user;
  if (!isBillingConfigured()) return null;
  if (Purchases.isConfigured()) {
    const instance = Purchases.getSharedInstance();
    void instance.changeUser(user.id);
    return instance;
  }
  return Purchases.configure({ apiKey: API_KEY as string, appUserId: user.id });
}

function instance(): Purchases | null {
  return Purchases.isConfigured() ? Purchases.getSharedInstance() : null;
}

/**
 * The annual package from the `pro` offering, or null when none is configured yet.
 *
 * Asked for by name rather than trusting whichever offering is marked current, so
 * experimenting with another offering in the dashboard cannot silently change what this
 * screen sells. The current offering is only a fallback.
 */
export async function annualPackage(): Promise<Package | null> {
  const purchases = instance();
  if (purchases == null) return null;
  const offerings = await purchases.getOfferings();
  const offering = offerings.all[PRO_OFFERING_ID] ?? offerings.current;
  if (offering == null) return null;
  return offering.annual ?? Object.values(offering.availablePackages)[0] ?? null;
}

/**
 * The server's own code for a refused checkout, e.g. `already_subscribed`.
 *
 * `subscribe.tsx` branches on these, so the code has to survive the trip rather than
 * whatever prose happened to be attached to it.
 */
function refusalCode(cause: unknown, fallback: string): string {
  if (cause instanceof ApiError) return cause.code;
  return cause instanceof Error ? cause.message : fallback;
}

export interface PurchaseOutcome {
  ok: boolean;
  /** Set when the server refused to mint an intent, e.g. another owner already subscribed. */
  error?: string;
  /**
   * RevenueCat's own code when the SDK refused the purchase.
   *
   * Kept separate from `error` because these are the failures a developer has to act on —
   * an unconfigured dashboard and a declined card are not the same problem, and collapsing
   * both into one sentence is what makes checkout impossible to debug from the UI.
   */
  errorCode?: ErrorCode;
}

/**
 * A sentence naming the actual refusal, rather than the catch-all.
 *
 * Unmapped codes still report their number: an unrecognised failure the user can quote is
 * worth far more than a generic apology.
 */
export function describePurchaseFailure(outcome: PurchaseOutcome): string {
  switch (outcome.errorCode) {
    case ErrorCode.ConfigurationError:
      return 'Checkout is not configured correctly. Check that the Stripe config in the RevenueCat dashboard is connected and points at the “pro” offering.';
    case ErrorCode.InvalidCredentialsError:
      return 'Checkout rejected this app’s credentials. Check that the Web SDK public key is the one for the Stripe config.';
    case ErrorCode.ProductNotAvailableForPurchaseError:
      return 'That plan is not available for purchase yet. Check the product is attached to the “pro” offering and is live.';
    case ErrorCode.ProductAlreadyPurchasedError:
      return 'This account has already bought that subscription.';
    case ErrorCode.PaymentPendingError:
      return 'The payment is still pending. Platewell unlocks as soon as it clears.';
    case ErrorCode.StoreProblemError:
      return 'The payment provider reported a problem. Nothing has been charged.';
    case ErrorCode.NetworkError:
      return 'Checkout could not be reached. Check your connection and try again.';
    case ErrorCode.InvalidEmailError:
      return 'That email address was rejected by checkout.';
    case undefined:
      break;
    default:
      return `The purchase didn’t complete (RevenueCat error ${outcome.errorCode}). Nothing has been charged.`;
  }

  switch (outcome.error) {
    case 'no_offering':
      return 'No subscription plan is available yet. Check the “pro” offering in the RevenueCat dashboard.';
    case 'manage_in_store':
      return 'This subscription was bought in the iOS or Android app. Update the payment method in your App Store or Google Play account.';
    case 'portal_unavailable':
    case 'reactivate_failed':
      return 'The billing portal couldn’t be opened. Please try again.';
    case 'billing_unavailable':
      return 'Checkout isn’t available right now. Please try again later.';
    case 'owner_required':
      return 'Only an owner of this household can subscribe.';
    default:
      return 'The purchase didn’t complete. Nothing has been charged.';
  }
}

/**
 * Buys the annual subscription for a household.
 *
 * The intent is minted **before** the purchase call and attached as a subscriber
 * attribute, so the webhook binds the purchase to this household deterministically rather
 * than guessing from the buyer's memberships. The server's 409 here is what stops two
 * owners of one household both paying.
 */
export async function subscribe(householdId: string): Promise<PurchaseOutcome> {
  const purchases = instance();
  if (purchases == null) return { ok: false, error: 'billing_unavailable' };

  let intentId: string;
  try {
    ({ intentId } = await createCheckout(householdId));
  } catch (cause) {
    return { ok: false, error: refusalCode(cause, 'checkout_failed') };
  }

  return purchaseWithIntent(purchases, householdId, intentId);
}

/**
 * Recovers a failed renewal by opening the Stripe portal to update the card.
 *
 * Not a fresh checkout: Stripe keeps retrying the lapsed subscription, and updating the
 * card pays its open invoice, so a second checkout would charge the customer twice. The
 * household unlocks when RevenueCat's `RENEWAL` webhook lands, which arrives over sync.
 */
export async function reactivate(householdId: string): Promise<PurchaseOutcome> {
  let url: string;
  try {
    ({ url } = await reactivateBilling(householdId));
  } catch (cause) {
    return { ok: false, error: refusalCode(cause, 'reactivate_failed') };
  }

  window.open(url, '_blank', 'noopener');
  return { ok: true };
}

async function purchaseWithIntent(
  purchases: Purchases,
  householdId: string,
  intentId: string,
): Promise<PurchaseOutcome> {
  const rcPackage = await annualPackage();
  if (rcPackage == null) return { ok: false, error: 'no_offering' };

  try {
    // Awaited, not fired and forgotten. RevenueCat snapshots these into the webhook
    // payload, so a purchase that races ahead of this request binds to no household and
    // the entitlement never lands. `setAttributes` also throws on failure, which an
    // unawaited call would turn into an unhandled rejection nobody sees.
    await purchases.setAttributes({
      platewell_household_id: householdId,
      platewell_intent_id: intentId,
      // Reserved attributes, so the name and email land on the RevenueCat customer itself
      // rather than as custom fields: this is what makes a subscriber searchable by who
      // they are in the dashboard, and what the receipt is addressed to.
      [ReservedCustomerAttribute.DisplayName]: buyer?.name ?? null,
      [ReservedCustomerAttribute.Email]: buyer?.email ?? null,
    });
    await purchases.purchase({
      rcPackage,
      // Pre-filling the email means checkout does not stop to ask for one it already has.
      customerEmail: buyer?.email ?? undefined,
      // Propagated to Stripe and the RevenueCat transaction, so support can go from a
      // Stripe charge to the household and back. Display only: the webhook still binds
      // through the subscriber attributes above, which carry the same ids.
      metadata: {
        platewell_household_id: householdId,
        platewell_user_id: buyer?.id ?? null,
        platewell_intent_id: intentId,
      },
    });
    return { ok: true };
  } catch (cause) {
    if (cause instanceof PurchasesError) {
      return { ok: false, error: 'purchase_failed', errorCode: cause.errorCode };
    }
    return { ok: false, error: cause instanceof Error ? cause.message : 'purchase_failed' };
  }
}

/** True when RevenueCat already considers this user entitled, e.g. after a reinstall. */
export async function hasProEntitlement(): Promise<boolean> {
  const purchases = instance();
  if (purchases == null) return false;
  const info: CustomerInfo = await purchases.getCustomerInfo();
  return info.entitlements.active[PRO_ENTITLEMENT_ID] != null;
}
